import { Signal } from "@preact/signals-core";
import type { ReadonlySignal } from "@preact/signals-core";
import { currentScope, effectScope } from "./scope";

export type BindValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Node
  | ReadonlySignal<unknown>
  | ((event: Event) => void);

const TOKEN_PREFIX = "@@b";
const TOKEN_SUFFIX = "@@";
const SOLE_TOKEN_RE = /^@@b(\d+)@@$/;

/** Always construct a fresh instance — a shared /g regex's mutable lastIndex corrupts interleaved calls. */
function tokenRegex(): RegExp {
  return /@@b(\d+)@@/g;
}

function isSignal(value: unknown): value is ReadonlySignal<unknown> {
  return value instanceof Signal;
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (isSignal(value)) return stringify(value.value);
  // BindValue only ever reaches here as string/number/boolean, never a plain object
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  return String(value);
}

const templateCache = new WeakMap<TemplateStringsArray, HTMLTemplateElement>();

function buildTemplate(strings: TemplateStringsArray): HTMLTemplateElement {
  let template = templateCache.get(strings);
  if (template) return template;

  let markup = "";
  strings.forEach((chunk, i) => {
    markup += chunk;
    if (i < strings.length - 1) markup += `${TOKEN_PREFIX}${i}${TOKEN_SUFFIX}`;
  });

  template = document.createElement("template");
  template.innerHTML = markup;
  templateCache.set(strings, template);
  return template;
}

/**
 * Tagged template: builds a DOM fragment, binding signals/handlers/nodes at their token positions.
 * Attribute interpolations must be quoted, e.g. `onclick="${fn}"`, `class="${signal}"`.
 */
export function html(strings: TemplateStringsArray, ...values: BindValue[]): DocumentFragment {
  const template = buildTemplate(strings);
  const fragment = template.content.cloneNode(true) as DocumentFragment;
  bindTree(fragment, values);
  return fragment;
}

function bindTree(root: DocumentFragment | Element, values: BindValue[]): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  const elements: Element[] = [];

  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) textNodes.push(node as Text);
    else elements.push(node as Element);
    node = walker.nextNode();
  }

  for (const el of elements) bindAttributes(el, values);
  for (const text of textNodes) bindTextNode(text, values);
}

function bindAttributes(el: Element, values: BindValue[]): void {
  for (const attr of Array.from(el.attributes)) {
    const soleMatch = attr.value.match(SOLE_TOKEN_RE);

    if (attr.name.startsWith("on") && soleMatch) {
      const handler = values[Number(soleMatch[1])];
      if (typeof handler === "function") {
        el.addEventListener(attr.name.slice(2), handler);
      }
      el.removeAttribute(attr.name);
      continue;
    }

    if (!tokenRegex().test(attr.value)) continue;

    const parts = splitTokens(attr.value);
    const name = attr.name;
    const compute = (): string =>
      parts.map((p) => (p.static ? p.text : stringify(values[p.index]))).join("");
    const hasSignal = parts.some((p) => !p.static && isSignal(values[p.index]));

    if (hasSignal) {
      currentScope().effect(() => {
        el.setAttribute(name, compute());
      });
    } else {
      el.setAttribute(name, compute());
    }
  }
}

function bindTextNode(textNode: Text, values: BindValue[]): void {
  if (!tokenRegex().test(textNode.data)) return;

  const parts = splitTokens(textNode.data);
  const parent = textNode.parentNode;
  if (!parent) return;

  const replacements: Node[] = parts.map((part) => {
    if (part.static) return document.createTextNode(part.text);
    const value = values[part.index];
    if (value instanceof Node) return value;
    if (isSignal(value)) {
      const node = document.createTextNode(stringify(value.value));
      currentScope().effect(() => {
        node.data = stringify(value.value);
      });
      return node;
    }
    return document.createTextNode(stringify(value));
  });

  for (const replacement of replacements) parent.insertBefore(replacement, textNode);
  parent.removeChild(textNode);
}

interface StaticPart {
  static: true;
  text: string;
}
interface ValuePart {
  static: false;
  index: number;
}

function splitTokens(input: string): (StaticPart | ValuePart)[] {
  const parts: (StaticPart | ValuePart)[] = [];
  let lastIndex = 0;
  const re = tokenRegex();
  let match: RegExpExecArray | null;
  while ((match = re.exec(input))) {
    if (match.index > lastIndex) parts.push({ static: true, text: input.slice(lastIndex, match.index) });
    parts.push({ static: false, index: Number(match[1]) });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < input.length) parts.push({ static: true, text: input.slice(lastIndex) });
  return parts;
}

export function bindText(el: Element | Text, signal: ReadonlySignal<unknown>): void {
  currentScope().effect(() => {
    if (el instanceof Text) el.data = stringify(signal.value);
    else el.textContent = stringify(signal.value);
  });
}

export function bindShow(el: HTMLElement, signal: ReadonlySignal<boolean>): void {
  currentScope().effect(() => {
    el.style.display = signal.value ? "" : "none";
  });
}

/** Error/status paragraphs (`.form-error` and its siblings like `.smtp-error`) sit
 * empty most of the time — `bindText` alone leaves them in the layout as a blank
 * element, which still costs a `.view`/`.card` flex `gap` even with no text to show.
 * Combines bindText + bindShow so the element (and its gap) collapses entirely when
 * the message is empty, and reappears with its text set the moment there's one. */
export function bindErrorText(el: HTMLElement, signal: ReadonlySignal<string>): void {
  currentScope().effect(() => {
    const text = signal.value;
    el.textContent = text;
    el.style.display = text ? "" : "none";
  });
}

export function bindClass(el: Element, name: string, signal: ReadonlySignal<boolean>): void {
  currentScope().effect(() => {
    el.classList.toggle(name, Boolean(signal.value));
  });
}

export interface BindValueOptions<T> {
  parse?: (raw: string) => T;
  format?: (value: T) => string;
}

export function bindValue(
  input: HTMLInputElement | HTMLTextAreaElement,
  signal: Signal<string>,
  opts: BindValueOptions<string> = {},
): void {
  const format = opts.format ?? ((v: string) => v);
  const parse = opts.parse ?? ((v: string) => v);

  currentScope().effect(() => {
    const formatted = format(signal.value);
    if (document.activeElement !== input && input.value !== formatted) input.value = formatted;
  });

  const onInput = (): void => {
    signal.value = parse(input.value);
  };
  input.addEventListener("input", onInput);
  currentScope().cleanup(() => input.removeEventListener("input", onInput));
}

/** Keyed reconciliation: reuses a row's DOM only while its item is the **same object
 * reference**. A key whose item reference changed re-renders from scratch (Phase 2a
 * item 13) — mutation responses put fresh objects in the signal and rows update
 * automatically; views must never hand-patch row DOM (that pattern produced two live
 * stale-row bugs in Phases 1–2). Rows mid-edit are unaffected: their item reference
 * only changes when a save replaces it. */
export function list<T>(
  container: Element,
  itemsSignal: Signal<T[]>,
  keyFn: (item: T) => string,
  renderFn: (item: T) => Node,
): void {
  const nodesByKey = new Map<string, Node>();
  const disposeByKey = new Map<string, () => void>();
  const itemByKey = new Map<string, T>();

  currentScope().effect(() => {
    const items = itemsSignal.value;
    const seen = new Set<string>();
    let previous: ChildNode | null = null;

    for (const item of items) {
      const key = keyFn(item);
      seen.add(key);
      let node = nodesByKey.get(key);
      if (node && itemByKey.get(key) !== item) {
        (node as ChildNode).remove();
        disposeByKey.get(key)?.();
        node = undefined;
      }
      if (!node) {
        // Each row gets its own effectScope — list() may (re-)render rows well after
        // the view's own initial construction (e.g. once an async load resolves), by
        // which point the ambient activeScope from that construction is long gone.
        // Without this, renderFn couldn't use bindText/bindShow/etc. at all. This also
        // gives each row proper cleanup when it's removed below (previously leaked).
        const scoped = effectScope(() => renderFn(item));
        node = scoped.node;
        nodesByKey.set(key, node);
        disposeByKey.set(key, scoped.dispose);
        itemByKey.set(key, item);
      }
      const target = previous ? previous.nextSibling : container.firstChild;
      if (target !== node) container.insertBefore(node, target);
      previous = node as ChildNode;
    }

    for (const [key, node] of nodesByKey) {
      if (!seen.has(key)) {
        (node as ChildNode).remove();
        nodesByKey.delete(key);
        disposeByKey.get(key)?.();
        disposeByKey.delete(key);
        itemByKey.delete(key);
      }
    }
  });
}
